import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import axios from "axios";
import {
  Plus, Search, Play, Square, RefreshCw, Monitor, Globe,
  Fingerprint, X, Maximize2, Minimize2, Edit, Trash2,
  Users, CheckCircle, XCircle, Loader, Shield, ShieldCheck,
  ShieldX, ShieldQuestion, Pencil, Check, ArrowLeftRight,
  RotateCw, ChevronLeft, ChevronRight, Lock, Zap
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DEFAULT_FOLDERS = ["Заявки", "Блок", "ФСП"];
const FOLDERS_STORAGE_KEY = "browser_profiles_folders_v1";
const PROFILE_FOLDER_STORAGE_KEY = "browser_profiles_folder_map_v1";
const COLUMN_WIDTHS_STORAGE_KEY = "browser_profiles_column_widths_v1";
const DEFAULT_COLUMN_WIDTHS = {
  index: 44,
  status: 124,
  name: 210,
  os: 130,
  proxy: 190,
  folder: 130,
  ip: 130,
  actions: 220,
};
const COLUMN_MIN_WIDTHS = {
  index: 36,
  status: 100,
  name: 160,
  os: 110,
  proxy: 150,
  folder: 110,
  ip: 90,
  actions: 170,
};

const TABLE_COLUMNS = [
  { key: "index", label: "", align: "left" },
  { key: "status", label: "Статус", align: "left" },
  { key: "name", label: "Имя профиля", align: "left" },
  { key: "os", label: "OS / Браузер", align: "left" },
  { key: "proxy", label: "Прокси", align: "left" },
  { key: "folder", label: "Папка", align: "left" },
  { key: "ip", label: "IP", align: "left" },
  { key: "actions", label: "Действия", align: "right" },
];

const OS_OPTIONS = ["Windows", "macOS", "Linux"];
const BROWSER_OPTIONS = ["Chrome", "Firefox", "Edge"];
const RESOLUTION_OPTIONS = ["1920x1080", "1366x768", "1536x864", "2560x1440"];

const USER_AGENT_MAP = {
  "Windows_Chrome": "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/124.0.0.0 Safari/537.36",
  "Windows_Firefox": "Mozilla/5.0 (Windows NT 10.0; Win64; x64; rv:125.0) Gecko/20100101 Firefox/125.0",
  "Windows_Edge": "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/124.0.0.0 Safari/537.36 Edg/124.0.0.0",
  "macOS_Chrome": "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/124.0.0.0 Safari/537.36",
  "macOS_Firefox": "Mozilla/5.0 (Macintosh; Intel Mac OS X 10.15; rv:125.0) Gecko/20100101 Firefox/125.0",
  "macOS_Edge": "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/124.0.0.0 Safari/537.36 Edg/124.0.0.0",
  "Linux_Chrome": "Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/124.0.0.0 Safari/537.36",
  "Linux_Firefox": "Mozilla/5.0 (X11; Linux x86_64; rv:125.0) Gecko/20100101 Firefox/125.0",
  "Linux_Edge": "Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/124.0.0.0 Safari/537.36 Edg/124.0.0.0",
};

function generateUserAgent(os, browser) {
  return USER_AGENT_MAP[`${os}_${browser}`] || USER_AGENT_MAP["Windows_Chrome"];
}

const emptyProfile = {
  name: "", user_agent: generateUserAgent("Windows", "Chrome"), proxy: "", cookies: "", notes: "",
  status: "active", os: "Windows", browser: "Chrome", screen_resolution: "1920x1080",
};

export default function BrowserProfiles() {
  const [profiles, setProfiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [runningBrowsers, setRunningBrowsers] = useState({});
  const [launchingId, setLaunchingId] = useState(null);
  const [viewerData, setViewerData] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [formData, setFormData] = useState({ ...emptyProfile });
  const [checkingProxy, setCheckingProxy] = useState({});
  const [editingProxyId, setEditingProxyId] = useState(null);
  const [editProxyValue, setEditProxyValue] = useState("");
  const proxyInputRef = useRef(null);
  const [folders, setFolders] = useState(DEFAULT_FOLDERS);
  const [profileFolders, setProfileFolders] = useState({});
  const [activeFolder, setActiveFolder] = useState("all");
  const [showFolderInput, setShowFolderInput] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [columnWidths, setColumnWidths] = useState(DEFAULT_COLUMN_WIDTHS);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  const fetchProfiles = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(`${API}/browser-profiles`, { headers: getAuthHeaders() });
      setProfiles(res.data || []);
    } catch {
      toast.error("Ошибка загрузки профилей");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchRunning = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/browser-profiles/running`, { headers: getAuthHeaders() });
      const map = {};
      (res.data || []).forEach((b) => { map[b.profile_id] = b; });
      setRunningBrowsers(map);
    } catch {}
  }, []);

  useEffect(() => { fetchProfiles(); fetchRunning(); }, [fetchProfiles, fetchRunning]);

  useEffect(() => {
    try {
      const rawFolders = localStorage.getItem(FOLDERS_STORAGE_KEY);
      const rawMap = localStorage.getItem(PROFILE_FOLDER_STORAGE_KEY);
      const parsedFolders = rawFolders ? JSON.parse(rawFolders) : [];
      const parsedMap = rawMap ? JSON.parse(rawMap) : {};
      if (Array.isArray(parsedFolders) && parsedFolders.length > 0) {
        const merged = Array.from(new Set([...DEFAULT_FOLDERS, ...parsedFolders]));
        setFolders(merged.filter((name) => typeof name === "string" && name.trim()));
      }
      if (parsedMap && typeof parsedMap === "object") setProfileFolders(parsedMap);
    } catch {
      setFolders(DEFAULT_FOLDERS);
      setProfileFolders({});
    }
  }, []);

  useEffect(() => { localStorage.setItem(FOLDERS_STORAGE_KEY, JSON.stringify(folders)); }, [folders]);
  useEffect(() => { localStorage.setItem(PROFILE_FOLDER_STORAGE_KEY, JSON.stringify(profileFolders)); }, [profileFolders]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(COLUMN_WIDTHS_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") setColumnWidths({ ...DEFAULT_COLUMN_WIDTHS, ...parsed });
    } catch {}
  }, []);

  useEffect(() => { localStorage.setItem(COLUMN_WIDTHS_STORAGE_KEY, JSON.stringify(columnWidths)); }, [columnWidths]);

  useEffect(() => {
    if (activeFolder !== "all" && !folders.includes(activeFolder)) setActiveFolder("all");
  }, [activeFolder, folders]);

  useEffect(() => {
    if (editingProxyId && proxyInputRef.current) {
      proxyInputRef.current.focus();
      proxyInputRef.current.select();
    }
  }, [editingProxyId]);

  const handleOsOrBrowserChange = (field, value) => {
    const next = { ...formData, [field]: value };
    next.user_agent = generateUserAgent(next.os, next.browser);
    setFormData(next);
  };

  const saveProfile = async () => {
    try {
      const payload = { ...formData };
      if (!payload.user_agent || !payload.user_agent.trim()) {
        payload.user_agent = generateUserAgent(payload.os || "Windows", payload.browser || "Chrome");
      }
      if (editingProfile) {
        const res = await axios.patch(`${API}/browser-profiles/${editingProfile.id}`, payload, { headers: getAuthHeaders() });
        setProfiles(profiles.map((p) => (p.id === editingProfile.id ? res.data : p)));
        toast.success("Профиль обновлён");
      } else {
        const res = await axios.post(`${API}/browser-profiles`, payload, { headers: getAuthHeaders() });
        setProfiles([res.data, ...profiles]);
        toast.success("Профиль создан");
      }
      closeModal();
    } catch {
      toast.error("Ошибка сохранения");
    }
  };

  const deleteProfile = async (id) => {
    if (!window.confirm("Удалить профиль?")) return;
    try {
      await axios.delete(`${API}/browser-profiles/${id}`, { headers: getAuthHeaders() });
      setProfiles(profiles.filter((p) => p.id !== id));
      toast.success("Профиль удалён");
    } catch {
      toast.error("Ошибка удаления");
    }
  };

  const openCreate = () => {
    setEditingProfile(null);
    setFormData({ ...emptyProfile });
    setShowModal(true);
  };

  const openEdit = (profile) => {
    setEditingProfile(profile);
    const data = { ...profile };
    if (!data.user_agent || !data.user_agent.trim()) {
      data.user_agent = generateUserAgent(data.os || "Windows", data.browser || "Chrome");
    }
    setFormData(data);
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditingProfile(null); };

  const checkProxy = async (profileId) => {
    setCheckingProxy((prev) => ({ ...prev, [profileId]: true }));
    try {
      const res = await axios.post(`${API}/browser-profiles/${profileId}/check-proxy`, {}, { headers: getAuthHeaders(), timeout: 20000 });
      const d = res.data;
      setProfiles((prev) =>
        prev.map((p) =>
          p.id === profileId ? { ...p, proxy_status: d.status, proxy_ip: d.ip, proxy_checked_at: new Date().toISOString() } : p
        )
      );
      if (d.status === "ok") toast.success(`Прокси работает (IP: ${d.ip})`);
      else if (d.status === "no_proxy") toast.info("Прямое подключение (без прокси)");
      else toast.error(`Прокси не работает: ${d.message || "Ошибка"}`);
    } catch {
      toast.error("Ошибка проверки прокси");
      setProfiles((prev) => prev.map((p) => (p.id === profileId ? { ...p, proxy_status: "error" } : p)));
    } finally {
      setCheckingProxy((prev) => ({ ...prev, [profileId]: false }));
    }
  };

  const checkAllProxies = async () => {
    const ids = profiles.map((p) => p.id);
    ids.forEach((id) => setCheckingProxy((prev) => ({ ...prev, [id]: true })));
    toast.info("Проверка всех прокси...");
    try {
      const res = await axios.post(`${API}/browser-profiles/check-all-proxies`, {}, { headers: getAuthHeaders(), timeout: 120000 });
      const results = res.data || [];
      setProfiles((prev) =>
        prev.map((p) => {
          const r = results.find((x) => x.profile_id === p.id);
          return r ? { ...p, proxy_status: r.status, proxy_ip: r.ip, proxy_checked_at: new Date().toISOString() } : p;
        })
      );
      const ok = results.filter((r) => r.status === "ok").length;
      const fail = results.filter((r) => r.status === "error").length;
      toast.success(`Проверено: ${ok} ОК, ${fail} ошибок`);
    } catch {
      toast.error("Ошибка проверки");
    } finally {
      ids.forEach((id) => setCheckingProxy((prev) => ({ ...prev, [id]: false })));
    }
  };

  const startEditProxy = (profile) => { setEditingProxyId(profile.id); setEditProxyValue(profile.proxy || ""); };

  const saveProxyInline = async (profileId) => {
    try {
      const res = await axios.patch(`${API}/browser-profiles/${profileId}`, { proxy: editProxyValue }, { headers: getAuthHeaders() });
      setProfiles((prev) => prev.map((p) => (p.id === profileId ? { ...res.data, proxy_status: undefined, proxy_ip: undefined } : p)));
      toast.success("Прокси обновлён");
    } catch { toast.error("Ошибка сохранения"); }
    setEditingProxyId(null);
  };

  const cancelEditProxy = () => { setEditingProxyId(null); };

  const createFolder = () => {
    const candidate = (newFolderName || "").trim();
    if (!candidate) { toast.error("Введите название папки"); return; }
    if (folders.some((f) => f.toLowerCase() === candidate.toLowerCase())) { toast.error("Такая папка уже существует"); return; }
    setFolders((prev) => [...prev, candidate]);
    setActiveFolder(candidate);
    setNewFolderName("");
    setShowFolderInput(false);
    toast.success(`Папка «${candidate}» создана`);
  };

  const assignProfileFolder = (profileId, folderName) => {
    if (!folderName) {
      setProfileFolders((prev) => { const next = { ...prev }; delete next[profileId]; return next; });
      return;
    }
    setProfileFolders((prev) => ({ ...prev, [profileId]: folderName }));
  };

  const getColumnStyle = (columnKey) => ({
    width: `${columnWidths[columnKey] || DEFAULT_COLUMN_WIDTHS[columnKey]}px`,
    minWidth: `${COLUMN_MIN_WIDTHS[columnKey]}px`,
    maxWidth: `${columnWidths[columnKey] || DEFAULT_COLUMN_WIDTHS[columnKey]}px`,
  });

  const startColumnResize = (columnKey, event) => {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = columnWidths[columnKey] || DEFAULT_COLUMN_WIDTHS[columnKey];
    const minWidth = COLUMN_MIN_WIDTHS[columnKey] || 60;
    const onMouseMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX;
      setColumnWidths((prev) => ({ ...prev, [columnKey]: Math.max(minWidth, startWidth + delta) }));
    };
    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  const openProxySelector = (profileId) => {
    toast.info("Выбор прокси временно недоступен. Используйте карандаш для ручного редактирования.");
  };

  const getThClass = (align = "left") =>
    `relative text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-3 ${align === "right" ? "text-right" : "text-left"}`;

  const getTdClass = (align = "left") =>
    `px-3 py-3 ${align === "right" ? "text-right" : "text-left"}`;

  const launchBrowser = async (profile) => {
    setLaunchingId(profile.id);
    toast.info(`Запуск: ${profile.name}...`);
    try {
      const res = await axios.post(
        `${API}/browser-profiles/${profile.id}/launch-real`,
        { target_url: "https://finance.ozon.ru/apps/auth/signin?isNotFirstEntryAttempt=true", cookies: profile.cookies || "" },
        { headers: getAuthHeaders(), timeout: 60000 }
      );
      if (res.data.success) {
        toast.success(`Браузер запущен: ${profile.name}`);
        setRunningBrowsers((prev) => ({ ...prev, [profile.id]: res.data }));
        if (res.data.novnc_url) {
          setViewerData({ profileId: profile.id, name: profile.name, novncUrl: res.data.novnc_url, wsPort: res.data.ws_port });
        }
      } else {
        toast.error(res.data.error || "Ошибка запуска");
      }
    } catch (err) {
      toast.error("Ошибка: " + (err.response?.data?.detail || err.message));
    } finally {
      setLaunchingId(null);
    }
  };

  const openViewer = (profileId) => {
    const info = runningBrowsers[profileId];
    if (info?.novnc_url) {
      const profile = profiles.find((p) => p.id === profileId);
      setViewerData({ profileId, name: profile?.name || profileId, novncUrl: info.novnc_url, wsPort: info.ws_port });
    }
  };

  const stopBrowser = async (profileId) => {
    try {
      await axios.post(`${API}/browser-profiles/${profileId}/stop`, {}, { headers: getAuthHeaders() });
      setRunningBrowsers((prev) => { const n = { ...prev }; delete n[profileId]; return n; });
      if (viewerData?.profileId === profileId) setViewerData(null);
      toast.success("Браузер остановлен");
    } catch { toast.error("Ошибка остановки"); }
  };

  const activeProfiles = profiles.filter((p) => p.status === "active");
  const inactiveProfiles = profiles.filter((p) => p.status === "inactive");
  const runningCount = Object.keys(runningBrowsers).length;

  const folderFiltered = profiles.filter((profile) => {
    if (activeFolder === "all") return true;
    return (profileFolders[profile.id] || "") === activeFolder;
  });

  const filtered = folderFiltered.filter((p) =>
    p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.proxy?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.notes?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const extractIp = (proxyStr) => {
    if (!proxyStr) return "";
    const m = proxyStr.match(/(\d+\.\d+\.\d+\.\d+)/);
    return m ? m[1] : "";
  };

  const ProxyStatusIcon = ({ profile }) => {
    const isChecking = checkingProxy[profile.id];
    const st = profile.proxy_status;
    if (isChecking) return <Loader className="w-4 h-4 text-blue-400 animate-spin flex-shrink-0" />;
    if (!profile.proxy) return <Globe className="w-4 h-4 text-gray-300 flex-shrink-0" />;
    if (st === "ok") return (
      <button onClick={() => checkProxy(profile.id)} title={`Прокси ОК (IP: ${profile.proxy_ip || "?"})\nНажмите для повторной проверки`} className="flex-shrink-0 hover:scale-110 transition">
        <ShieldCheck className="w-4 h-4 text-green-500" />
      </button>
    );
    if (st === "error") return (
      <button onClick={() => checkProxy(profile.id)} title="Прокси не работает\nНажмите для повторной проверки" className="flex-shrink-0 hover:scale-110 transition">
        <ShieldX className="w-4 h-4 text-red-500" />
      </button>
    );
    return (
      <button onClick={() => checkProxy(profile.id)} title="Проверить прокси" className="flex-shrink-0 hover:scale-110 transition">
        <ShieldQuestion className="w-4 h-4 text-gray-400 hover:text-blue-500" />
      </button>
    );
  };

  return (
    <div className="p-6 max-w-full">
      {/* Stat Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 shadow-lg shadow-blue-500/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/20 backdrop-blur flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-xs text-blue-100">Всего профилей</p>
              <p className="text-2xl font-bold text-white">{profiles.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-4 shadow-lg shadow-emerald-500/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/20 backdrop-blur flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-xs text-emerald-100">Активные</p>
              <p className="text-2xl font-bold text-white">{activeProfiles.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-violet-500 to-violet-600 rounded-xl p-4 shadow-lg shadow-violet-500/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/20 backdrop-blur flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-xs text-violet-100">Запущены</p>
              <p className="text-2xl font-bold text-white">{runningCount}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-rose-500 to-rose-600 rounded-xl p-4 shadow-lg shadow-rose-500/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/20 backdrop-blur flex items-center justify-center">
              <XCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-xs text-rose-100">Заблокированы</p>
              <p className="text-2xl font-bold text-white">{inactiveProfiles.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Section Header */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 mb-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/25">
              <Fingerprint className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Антидетект браузер</h2>
              <p className="text-sm text-gray-400">Управление профилями браузеров</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={checkAllProxies} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-emerald-600 border border-emerald-200 rounded-lg hover:bg-emerald-50 transition" data-testid="check-all-proxies-btn">
              <Shield className="w-4 h-4" /> Проверить прокси
            </button>
            <button onClick={() => { fetchProfiles(); fetchRunning(); }} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition" data-testid="refresh-profiles-btn">
              <RefreshCw className="w-4 h-4" /> Обновить
            </button>
            <button onClick={openCreate} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg hover:from-blue-700 hover:to-indigo-700 shadow-md shadow-blue-500/25 transition" data-testid="create-profile-btn">
              <Plus className="w-4 h-4" /> Добавить профиль
            </button>
          </div>
        </div>
      </div>

      {/* Folders */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 mb-4 shadow-sm">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-700 mb-2">Папки профилей</p>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setActiveFolder("all")} className={`px-3 py-1.5 text-xs rounded-lg border transition ${activeFolder === "all" ? "bg-blue-50 border-blue-200 text-blue-700 shadow-sm" : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"}`}>
                Все ({profiles.length})
              </button>
              {folders.map((folder) => {
                const count = profiles.filter((p) => (profileFolders[p.id] || "") === folder).length;
                return (
                  <button key={folder} onClick={() => setActiveFolder(folder)} className={`px-3 py-1.5 text-xs rounded-lg border transition ${activeFolder === folder ? "bg-blue-50 border-blue-200 text-blue-700 shadow-sm" : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"}`}>
                    {folder} ({count})
                  </button>
                );
              })}
            </div>
          </div>
          <div className="min-w-[280px] flex justify-end">
            {showFolderInput ? (
              <div className="flex items-center gap-2 w-full max-w-[320px]">
                <input className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") createFolder(); if (e.key === "Escape") { setShowFolderInput(false); setNewFolderName(""); } }} placeholder="Новая папка" />
                <button onClick={createFolder} className="px-3 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition">Создать</button>
                <button onClick={() => { setShowFolderInput(false); setNewFolderName(""); }} className="px-3 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition">Отмена</button>
              </div>
            ) : (
              <button onClick={() => setShowFolderInput(true)} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition">
                <Plus className="w-4 h-4" /> Создать папку
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl border border-gray-100 p-3 mb-4 shadow-sm">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input className="w-full pl-10 pr-4 py-2 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400" placeholder="Поиск по телефону, названию или имени..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} data-testid="search-profiles-input" />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <table className="w-full table-fixed">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/80">
              {TABLE_COLUMNS.map((col) => (
                <th key={col.key} className={getThClass(col.align)} style={getColumnStyle(col.key)}>
                  {col.label}
                  <div onMouseDown={(e) => startColumnResize(col.key, e)} className="absolute right-0 top-0 h-full w-2 cursor-col-resize select-none" title="Потяните, чтобы изменить ширину" />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan="8" className="text-center py-16 text-gray-400 text-sm"><Loader className="w-5 h-5 animate-spin mx-auto mb-2" />Загрузка...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan="8" className="text-center py-16 text-gray-400 text-sm">Нет профилей в выбранной папке.</td></tr>
            ) : (
              filtered.map((profile, idx) => {
                const isRunning = !!runningBrowsers[profile.id];
                const isLaunching = launchingId === profile.id;
                const isEditingProxy = editingProxyId === profile.id;
                const ip = profile.proxy_status === "ok" && profile.proxy_ip ? profile.proxy_ip : extractIp(profile.proxy);
                return (
                  <tr key={profile.id} className={`border-b border-gray-50 hover:bg-blue-50/30 transition-colors ${isRunning ? "bg-green-50/40" : ""}`} data-testid={`profile-row-${profile.id}`}>
                    <td className={`${getTdClass()} text-xs text-gray-400`} style={getColumnStyle("index")}>{idx + 1}</td>
                    <td className={getTdClass()} style={getColumnStyle("status")}>
                      {isRunning ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full"><span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> Запущен</span>
                      ) : profile.status === "active" ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full"><CheckCircle className="w-3 h-3" /> Активный</span>
                      ) : profile.status === "inactive" ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600 bg-red-50 px-2 py-1 rounded-full"><XCircle className="w-3 h-3" /> Неактивный</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-yellow-600 bg-yellow-50 px-2 py-1 rounded-full"><Loader className="w-3 h-3" /> Заявка</span>
                      )}
                    </td>
                    <td className={getTdClass()} style={getColumnStyle("name")}>
                      <p className="text-sm font-medium text-gray-800">{profile.name}</p>
                      {profile.notes && <p className="text-xs text-gray-400 mt-0.5">{profile.notes}</p>}
                    </td>
                    <td className={getTdClass()} style={getColumnStyle("os")}>
                      <span className="text-sm text-gray-600">{profile.os || "Windows"} / {profile.browser || "Chrome"}</span>
                    </td>
                    <td className={getTdClass()} style={getColumnStyle("proxy")}>
                      {isEditingProxy ? (
                        <div className="flex items-center gap-1.5">
                          <input ref={proxyInputRef} className="flex-1 border border-blue-300 rounded px-2 py-1 text-xs font-mono bg-blue-50/50 focus:outline-none focus:ring-1 focus:ring-blue-400 min-w-[110px]" value={editProxyValue} onChange={(e) => setEditProxyValue(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") saveProxyInline(profile.id); if (e.key === "Escape") cancelEditProxy(); }} placeholder="http://user:pass@ip:port" />
                          <button onClick={() => saveProxyInline(profile.id)} className="p-1 text-green-600 hover:bg-green-50 rounded transition" title="Сохранить"><Check className="w-3.5 h-3.5" /></button>
                          <button onClick={cancelEditProxy} className="p-1 text-gray-400 hover:bg-gray-100 rounded transition" title="Отмена"><X className="w-3.5 h-3.5" /></button>
                        </div>
                      ) : (
                        <div className="w-full flex items-center gap-1.5">
                          <button onClick={() => startEditProxy(profile)} className="inline-flex items-center justify-center text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded p-1 transition flex-shrink-0" title="Редактировать прокси"><Pencil className="w-3.5 h-3.5" /></button>
                          <ProxyStatusIcon profile={profile} className="flex-shrink-0" />
                          {profile.proxy ? (
                            <span className="flex-1 min-w-0 truncate text-xs text-gray-500 font-mono" title={profile.proxy}>{profile.proxy}</span>
                          ) : (
                            <span className="flex-1 min-w-0 truncate text-xs text-gray-400">Прямое подключение</span>
                          )}
                          <button onClick={() => openProxySelector(profile.id)} className="inline-flex items-center justify-center text-gray-400 hover:text-green-600 hover:bg-green-50 rounded p-1 transition flex-shrink-0" title="Сменить прокси"><ArrowLeftRight className="w-3.5 h-3.5" /></button>
                        </div>
                      )}
                    </td>
                    <td className={getTdClass()} style={getColumnStyle("folder")}>
                      <select value={profileFolders[profile.id] || ""} onChange={(e) => assignProfileFolder(profile.id, e.target.value)} className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-100">
                        <option value="">Без папки</option>
                        {folders.map((folder) => <option key={folder} value={folder}>{folder}</option>)}
                      </select>
                    </td>
                    <td className={getTdClass()} style={getColumnStyle("ip")}>
                      {profile.proxy_status === "ok" && profile.proxy_ip ? (
                        <span className="text-xs text-green-600 font-mono font-medium">{profile.proxy_ip}</span>
                      ) : profile.proxy_status === "error" ? (
                        <span className="text-xs text-red-400 font-mono">—</span>
                      ) : (
                        <span className="text-xs text-gray-500 font-mono">{ip || "—"}</span>
                      )}
                    </td>
                    <td className={getTdClass("right")} style={getColumnStyle("actions")}>
                      <div className="flex items-center justify-end gap-1.5">
                        {isRunning ? (
                          <>
                            <button onClick={() => openViewer(profile.id)} className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 px-2.5 py-1.5 rounded-lg transition"><Monitor className="w-3.5 h-3.5" /> Открыть</button>
                            <button onClick={() => stopBrowser(profile.id)} className="inline-flex items-center gap-1 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 px-2.5 py-1.5 rounded-lg transition" data-testid={`stop-btn-${profile.id}`}><Square className="w-3.5 h-3.5" /> Стоп</button>
                          </>
                        ) : (
                          <button onClick={() => launchBrowser(profile)} disabled={isLaunching} className={`inline-flex items-center gap-1 text-xs font-medium text-white bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 px-3 py-1.5 rounded-lg shadow-sm transition ${isLaunching ? "opacity-50" : ""}`} data-testid={`launch-btn-${profile.id}`}>
                            {isLaunching ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                            {isLaunching ? "Запуск..." : "Запустить"}
                          </button>
                        )}
                        <button onClick={() => openEdit(profile)} className="p-1.5 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition" title="Редактировать" data-testid={`edit-btn-${profile.id}`}><Edit className="w-4 h-4" /></button>
                        <button onClick={() => deleteProfile(profile.id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition" title="Удалить" data-testid={`delete-btn-${profile.id}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Modern Browser Viewer */}
      {viewerData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setViewerData(null)} />
          <div className={`relative bg-[#1a1a2e] border border-[#2a2a4a] shadow-2xl flex flex-col transition-all duration-300 ${isFullscreen ? "w-full h-full" : "rounded-2xl w-[calc(100%-48px)] h-[calc(100%-48px)] max-w-[1400px]"}`} data-testid="browser-viewer-modal">
            {/* Title bar */}
            <div className={`flex items-center justify-between px-4 py-2 bg-[#16162a] border-b border-[#2a2a4a] flex-shrink-0 ${isFullscreen ? "" : "rounded-t-2xl"}`}>
              <div className="flex items-center gap-3">
                <div className="flex gap-2">
                  <button onClick={() => stopBrowser(viewerData.profileId)} className="w-3.5 h-3.5 rounded-full bg-[#ff5f57] hover:bg-[#ff3b30] transition group relative" title="Закрыть">
                    <X className="w-2 h-2 text-[#aa0000] absolute inset-0 m-auto opacity-0 group-hover:opacity-100 transition" />
                  </button>
                  <button onClick={() => setViewerData(null)} className="w-3.5 h-3.5 rounded-full bg-[#febc2e] hover:bg-[#f5a623] transition group relative" title="Свернуть">
                    <Minimize2 className="w-2 h-2 text-[#995700] absolute inset-0 m-auto opacity-0 group-hover:opacity-100 transition" />
                  </button>
                  <button onClick={() => setIsFullscreen(!isFullscreen)} className="w-3.5 h-3.5 rounded-full bg-[#28c840] hover:bg-[#1aab29] transition group relative" title={isFullscreen ? "Уменьшить" : "На весь экран"}>
                    <Maximize2 className="w-2 h-2 text-[#006500] absolute inset-0 m-auto opacity-0 group-hover:opacity-100 transition" />
                  </button>
                </div>
                {/* Navigation */}
                <div className="flex items-center gap-1 ml-2">
                  <button className="p-1 rounded-md hover:bg-white/10 transition text-gray-500"><ChevronLeft className="w-4 h-4" /></button>
                  <button className="p-1 rounded-md hover:bg-white/10 transition text-gray-500"><ChevronRight className="w-4 h-4" /></button>
                  <button className="p-1 rounded-md hover:bg-white/10 transition text-gray-500"><RotateCw className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              {/* URL Bar */}
              <div className="flex-1 mx-4 max-w-2xl">
                <div className="flex items-center gap-2 bg-[#0d0d1a] border border-[#2a2a4a] rounded-lg px-3 py-1.5">
                  <Lock className="w-3.5 h-3.5 text-green-400 flex-shrink-0" />
                  <span className="text-xs text-gray-400 truncate font-mono">{viewerData.name} — finance.ozon.ru</span>
                </div>
              </div>
              {/* Actions */}
              <div className="flex items-center gap-2">
                <button onClick={() => setIsFullscreen(!isFullscreen)} className="p-1.5 hover:bg-white/10 rounded-lg transition text-gray-400">
                  {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                </button>
                <button onClick={() => stopBrowser(viewerData.profileId)} className="inline-flex items-center gap-1 text-xs text-red-400 hover:bg-red-500/10 px-2.5 py-1.5 rounded-lg transition font-medium">
                  <Square className="w-3 h-3" /> Стоп
                </button>
                <button onClick={() => setViewerData(null)} className="text-gray-500 hover:text-gray-300 p-1 transition"><X className="w-4 h-4" /></button>
              </div>
            </div>
            {/* Content */}
            <div className="flex-1 bg-[#0d0d1a] overflow-hidden rounded-b-2xl">
              <iframe src={viewerData.novncUrl} className="w-full h-full border-0" title="noVNC Browser" allow="clipboard-read; clipboard-write" />
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={closeModal}>
          <div className="bg-white rounded-2xl shadow-2xl w-[520px] max-h-[85vh] overflow-y-auto border border-gray-100" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white rounded-t-2xl">
              <h3 className="text-lg font-semibold text-gray-800">{editingProfile ? "Редактировать профиль" : "Добавить профиль"}</h3>
              <p className="text-sm text-gray-400 mt-0.5">Заполните данные для антидетект-браузера</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-sm text-gray-600 font-medium mb-1.5 block">Имя профиля</label>
                <input className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="79001234567" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-sm text-gray-600 font-medium mb-1.5 block">OS</label>
                  <select className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 transition" value={formData.os} onChange={(e) => handleOsOrBrowserChange("os", e.target.value)}>{OS_OPTIONS.map((o) => <option key={o}>{o}</option>)}</select>
                </div>
                <div>
                  <label className="text-sm text-gray-600 font-medium mb-1.5 block">Браузер</label>
                  <select className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 transition" value={formData.browser} onChange={(e) => handleOsOrBrowserChange("browser", e.target.value)}>{BROWSER_OPTIONS.map((b) => <option key={b}>{b}</option>)}</select>
                </div>
                <div>
                  <label className="text-sm text-gray-600 font-medium mb-1.5 block">Разрешение</label>
                  <select className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 transition" value={formData.screen_resolution} onChange={(e) => setFormData({ ...formData, screen_resolution: e.target.value })}>{RESOLUTION_OPTIONS.map((r) => <option key={r}>{r}</option>)}</select>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm text-gray-600 font-medium">User Agent</label>
                  <button type="button" onClick={() => setFormData({ ...formData, user_agent: generateUserAgent(formData.os, formData.browser) })} className="text-xs text-blue-500 hover:text-blue-700 transition font-medium" title="Сгенерировать на основе OS и браузера">
                    <RefreshCw className="w-3 h-3 inline mr-1" />Сгенерировать
                  </button>
                </div>
                <input className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm font-mono text-gray-600 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition" value={formData.user_agent} onChange={(e) => setFormData({ ...formData, user_agent: e.target.value })} placeholder="Генерируется автоматически" />
                <p className="text-xs text-gray-400 mt-1">Генерируется автоматически при выборе OS/браузера. Можно ввести вручную.</p>
              </div>
              <div>
                <label className="text-sm text-gray-600 font-medium mb-1.5 block">Прокси</label>
                <input className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition" value={formData.proxy} onChange={(e) => setFormData({ ...formData, proxy: e.target.value })} placeholder="http://user:pass@ip:port" />
              </div>
              <div>
                <label className="text-sm text-gray-600 font-medium mb-1.5 block">Cookies (JSON)</label>
                <textarea className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm font-mono h-24 resize-none focus:outline-none focus:ring-2 focus:ring-blue-100 transition" value={formData.cookies} onChange={(e) => setFormData({ ...formData, cookies: e.target.value })} placeholder='[{"name":"session","value":"abc","domain":".ozon.ru"}]' />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-gray-600 font-medium mb-1.5 block">Статус</label>
                  <select className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 transition" value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}>
                    <option value="active">Активный</option>
                    <option value="inactive">Неактивный</option>
                    <option value="request">Заявка</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-600 font-medium mb-1.5 block">Заметки</label>
                  <input className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 transition" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Доп. информация" />
                </div>
              </div>
            </div>
            <div className="flex gap-3 p-6 border-t border-gray-100 bg-gray-50/50 rounded-b-2xl">
              <button onClick={saveProfile} className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-sm font-medium py-2.5 rounded-lg shadow-md shadow-blue-500/25 transition">{editingProfile ? "Сохранить" : "Создать"}</button>
              <button onClick={closeModal} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm font-medium py-2.5 rounded-lg transition">Отмена</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
